import type { PostMessageSubmission, PostMessageSubProvider } from './types';

/**
 * Pipedrive Web Forms embed detection.
 *
 * Pipedrive embeds send postMessages to the parent window with numeric event types:
 *   { type: <number>, payload: { ..., uuid: "<form-uuid>", source: "pipedriveWebForms" } }
 *
 * Event types (from Pipedrive's Dc enum):
 *   0 = RESIZE      — iframe resize
 *   1 = INIT        — form loaded (payload: { version: "V2" })
 *   6 = REDIRECT    — form submitted, redirect requested (payload: { url: "..." })
 *
 * Pipedrive forms have two post-submission modes:
 *   - REDIRECT: sends type=6 with a redirect URL → we detect this as submission
 *   - THANK_YOU_PAGE: shows inline success screen, no postMessage fired
 *
 * Only the REDIRECT mode is detectable via postMessage.
 */

const PIPEDRIVE_TYPE_REDIRECT = 6;

export const PipedriveProvider: PostMessageSubProvider = {
    name: 'pipedrive',
    trustedOrigins: /^https:\/\/([\w-]+\.)?pipedrive\.com$/,

    detect(data: Record<string, unknown>): boolean {
        const payload = data?.payload as Record<string, unknown> | undefined;
        return payload?.source === 'pipedriveWebForms';
    },

    isSubmission(data: Record<string, unknown>): boolean {
        return data?.type === PIPEDRIVE_TYPE_REDIRECT;
    },

    extractData(data: Record<string, unknown>, origin: string): PostMessageSubmission {
        const payload = (data?.payload || {}) as Record<string, unknown>;
        const uuid = (payload?.uuid as string) || '';
        const redirectUrl = (payload?.url as string) || '';

        const indicators = ['postmessage_pipedrive_form_submitted'];
        if (uuid) {
            indicators.push(`pipedrive_form_uuid:${uuid}`);
        }
        if (redirectUrl) {
            try {
                const url = new URL(redirectUrl);
                indicators.push(`pipedrive_redirect_path:${url.pathname}`);
            } catch {
                // invalid URL, skip indicator
            }
        }

        return {
            provider: 'pipedrive',
            formId: uuid ? `pipedrive_form_${uuid}` : 'pipedrive_form',
            applyFormIdWhitelist: Boolean(uuid),
            indicators,
            origin,
        };
    },
};
